import { useState, useEffect, useRef } from 'react'
import { IntlShape } from 'react-intl'
import {useQuery, useMutation, useQueryClient} from 'react-query'
import {toast} from 'react-toastify'
import {Modal, Tooltip, OverlayTrigger} from 'react-bootstrap'
import {ID, KTSVG} from '../../../../../../../_dashboard/helpers'
import { getAll, createOne, patchOne, deleteOne } from '../../../../../../services/common_requests'
import ExtrasForm, {TourExtra} from './ExtrasForm'
import { useConfirm } from '../../../../../../components/ConfirmProvider'

function resolveCanonicalExtraRoot(
  translations: { language?: string; name?: string; description?: string }[],
  fallback?: { name?: string; description?: string },
) {
  const fr = translations.find((t) => t.language === 'fr')
  const en = translations.find((t) => t.language === 'en')
  const canonical = fr ?? en ?? translations[0]
  return {
    name: canonical?.name?.trim() || fallback?.name?.trim() || '',
    description: canonical?.description ?? fallback?.description ?? '',
  }
}

function resolveExtraDisplayName(
  item: TourExtra,
  lang: string,
): string {
  const lc = lang.trim().toLowerCase()
  const order = [lc, 'fr', 'en', 'de']
  for (const code of order) {
    const match = item.translations?.find(
      (t) => String(t.language ?? '').trim().toLowerCase() === code,
    )
    if (match?.name?.trim()) return match.name.trim()
  }
  return item.name?.trim() || ''
}

// Import Metronic menu
declare const KTMenu: any;

interface ExtrasProps {
  tourId: string
  tourPriceId?: string
  intl: IntlShape
  selectedLanguage: string
}

const Extras: React.FC<ExtrasProps> = ({tourId, tourPriceId, intl, selectedLanguage}) => {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [currentItem, setCurrentItem] = useState<TourExtra | null>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [showDropdownId, setShowDropdownId] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const confirm = useConfirm()

  // Fetch tour pricing data if tourPriceId is not provided
  const {data: pricingData, isLoading: isPricingLoading} = useQuery(
    ['tourPricing', tourId],
    () => getAll(null, `/tour-prices/by-tour/${tourId}`),
    {
      enabled: !!tourId && !tourPriceId,
      retry: 1,
      onError: (error) => {
        console.error('Fetch pricing error:', error)
        toast.error(intl.formatMessage({id: 'TOUR.PRICING.FETCH_ERROR'}))
      },
    }
  )

  // Determine the effective tour price ID
  const effectiveTourPriceId = tourPriceId || pricingData?.data?._id

  // Fetch extras only if we have a valid tourPriceId
  const {data: extrasData, isLoading: isExtrasLoading} = useQuery(
    ['tourExtras', effectiveTourPriceId, selectedLanguage],
    () => getAll(
      { language: selectedLanguage }, 
      `/tour-extra-prices/?tourPriceId=${effectiveTourPriceId}`
    ),
    {
      enabled: !!effectiveTourPriceId,
      retry: 1,
      onError: (error) => {
        console.error('Fetch extras error:', error)
        toast.error(intl.formatMessage({id: 'TOUR.EXTRAS.FETCH_ERROR'}))
      },
    }
  )

  // Create mutation
  const createMutation = useMutation(
    (data: Omit<TourExtra, '_id' | 'createdAt' | 'updatedAt'>) =>
      createOne(data, '/tour-extra-prices'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourExtras', effectiveTourPriceId])
        toast.success(intl.formatMessage({id: 'TOUR.EXTRAS.CREATE_SUCCESS'}))
        closeModal()
        setIsSaving(false)
      },
      onError: (error) => {
        console.error('Create error:', error)
        toast.error(intl.formatMessage({id: 'TOUR.EXTRAS.CREATE_ERROR'}))
        setIsSaving(false)
      },
      retry: false,
    }
  )

  // Update mutation
  const updateMutation = useMutation(
    ({id, data}: {id: string; data: Partial<TourExtra>}) =>
      patchOne(data, `/tour-extra-prices/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourExtras', effectiveTourPriceId])
        toast.success(intl.formatMessage({id: 'TOUR.EXTRAS.UPDATE_SUCCESS'}))
        closeModal()
        setIsSaving(false)
      },
      onError: (error) => {
        console.error('Update error:', error)
        toast.error(intl.formatMessage({id: 'TOUR.EXTRAS.UPDATE_ERROR'}))
        setIsSaving(false)
      },
      retry: false,
    }
  )

  // Delete mutation
  const deleteMutation = useMutation((id: ID) => deleteOne(id, `/tour-extra-prices`), {
    onSuccess: () => {
      queryClient.invalidateQueries(['tourExtras', effectiveTourPriceId])
      toast.success(intl.formatMessage({id: 'TOUR.EXTRAS.DELETE_SUCCESS'}))
    },
    onError: (error) => {
      console.error('Delete error:', error)
      toast.error(intl.formatMessage({id: 'TOUR.EXTRAS.DELETE_ERROR'}))
    },
    retry: false,
  })

  // Handle form submission
  const handleSubmit = (values: TourExtra) => {
    if (!effectiveTourPriceId) {
      toast.error(intl.formatMessage({id: 'TOUR.EXTRAS.NO_TOUR_PRICE_ID'}))
      return
    }

    setIsSaving(true)

    // Ensure all numeric values are numbers, not strings
    const amount = Number(values.amount)

    // Handle translations
    const translations = values.translations || []
    const existingTranslation = translations.find(t => t.language === selectedLanguage)
    
    const translationData = {
      language: selectedLanguage,
      name: values.name,
      description: values.description
    }

    if (existingTranslation) {
      // Update existing translation
      existingTranslation.name = values.name
      existingTranslation.description = values.description
    } else {
      // Add new translation
      translations.push(translationData)
    }

    const { name: rootName, description: rootDescription } = resolveCanonicalExtraRoot(
      translations,
      { name: currentItem?.name, description: currentItem?.description },
    )

    const payload = {
      ...values,
      name: rootName,
      description: rootDescription,
      tourPrice: effectiveTourPriceId,
      amount,
      translations
    }

    if (currentItem?._id) {
      updateMutation.mutate({
        id: currentItem._id,
        data: payload,
      })
    } else {
      createMutation.mutate(payload as Omit<TourExtra, '_id' | 'createdAt' | 'updatedAt'>)
    }
  }

  // Handle opening the modal for adding a new item
  const handleAddNew = () => {
    setCurrentItem({
      tourPrice: effectiveTourPriceId!,
      type: 'extra',
      name: '',
      amount: 0,
      frequency: 'per_person',
      applicationMode: 'PER_PERSON',
      description: '',
      isOptional: true,
      translations: []
    })
    setModalOpen(true)
  }

  // Handle opening the modal for editing an existing item
  const handleEdit = (item: TourExtra) => {
    // Find translation for current language
    const translation = item.translations?.find(t => t.language === selectedLanguage)
    
    // Set current item with translated values if they exist
    setCurrentItem({
      ...item,
      name: translation?.name || item.name,
      description: translation?.description || item.description
    })
    setModalOpen(true)
    setShowDropdownId(null)
  }

  // Close modal and reset state
  const closeModal = () => {
    setModalOpen(false)
    setCurrentItem(null)
  }

  // Toggle dropdown visibility
  const toggleDropdown = (id: string) => {
    setShowDropdownId(showDropdownId === id ? null : id)
  }

  // Handle delete
  const handleDelete = async (id: string) => {
    const ok = await confirm({ message: intl.formatMessage({id: 'TOUR.EXTRAS.DELETE_CONFIRM'}) })
    if (!ok) return
    deleteMutation.mutate(id as unknown as ID)
    setShowDropdownId(null)
  }

  // Format amount for display
  const formatAmount = (extra: TourExtra) => {
    return `${extra.amount} €`
  }

  // Format frequency for display
  const formatFrequency = (frequency: string) => {
    switch (frequency) {
      case 'per_person':
        return intl.formatMessage({id: 'TOUR.EXTRAS.FREQUENCY_PER_PERSON'})
      case 'per_booking':
        return intl.formatMessage({id: 'TOUR.EXTRAS.FREQUENCY_PER_BOOKING'})
      case 'per_day':
        return intl.formatMessage({id: 'TOUR.EXTRAS.FREQUENCY_PER_DAY'})
      case 'one_shot':
        return intl.formatMessage({id: 'TOUR.EXTRAS.FREQUENCY_ONE_TIME'})
      default:
        return frequency
    }
  }

  // Format application mode for display
  const formatApplicationMode = (mode: string) => {
    switch (mode) {
      case 'PER_PERSON':
        return intl.formatMessage({id: 'TOUR.PRICING.PER_PERSON'})
      case 'PER_TOUR':
        return intl.formatMessage({id: 'TOUR.PRICING.PER_TOUR'})
      default:
        return mode
    }
  }

  // Initialize Metronic menu
  useEffect(() => {
    // Initialize all dropdown menus
    const menuElements = document.querySelectorAll('[data-kt-menu="true"]')
    menuElements.forEach((menu) => {
      if (typeof KTMenu !== 'undefined') {
        const menuInstance = new KTMenu(menu)
        menuInstance.on('kt.menu.dropdown.show', function() {
          // Handle menu show
        })
      }
    })
  }, [extrasData]) // Re-initialize when data changes

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdownId(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Render the list view
  const renderListView = () => {
    const items = extrasData?.data || []
    const isLoading = isExtrasLoading

    return (
      <>
        <div className='d-flex justify-content-end mb-6'>
          <button type='button' className='btn btn-primary' onClick={handleAddNew}>
            <i className='ki-solid ki-plus fs-2'></i>
            {intl.formatMessage({id: 'TOUR.EXTRAS.ADD'})}
          </button>
        </div>
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-5'>
            <thead>
              <tr className='text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0'>
                <th>{intl.formatMessage({id: 'TOUR.EXTRAS.NAME'})}</th>
                <th>{intl.formatMessage({id: 'TOUR.EXTRAS.AMOUNT'})}</th>
                <th>{intl.formatMessage({id: 'TOUR.EXTRAS.FREQUENCY'})}</th>
                <th>{intl.formatMessage({id: 'TOUR.EXTRAS.APPLICATION_MODE'})}</th>
                <th>{intl.formatMessage({id: 'TOUR.EXTRAS.IS_OPTIONAL'})}</th>
                <th className='text-end'>{intl.formatMessage({id: 'COMMON.ACTIONS'})}</th>
              </tr>
            </thead>
            <tbody className='fw-semibold text-gray-600'>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className='text-center py-10'>
                    <div className='d-flex flex-column justify-content-center align-items-center'>
                      <div className='spinner-border text-primary' style={{ width: '2.5rem', height: '2.5rem' }} role='status'>
                        <span className='visually-hidden'>Loading...</span>
                      </div>
                      <div className='mt-3'>
                        <span className='text-gray-600 fs-6'>
                          {intl.formatMessage({id: 'COMMON.LOADING'})}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className='text-center py-10'>
                    <h3 className='fs-4 fw-bold mb-5'>
                      {intl.formatMessage({id: 'TOUR.EXTRAS.NO_EXTRAS'})}
                    </h3>
                    <p className='fs-6 text-gray-700'>
                      {intl.formatMessage({id: 'TOUR.EXTRAS.ADD_DESCRIPTION'})}
                    </p>
                  </td>
                </tr>
              ) : (
                items.map((item: TourExtra) => (
                  <tr key={item._id}>
                    <td>{resolveExtraDisplayName(item, selectedLanguage)}</td>
                    <td>{formatAmount(item)}</td>
                    <td>{formatFrequency(item.frequency)}</td>
                    <td>{formatApplicationMode(item.applicationMode)}</td>
                    <td>
                      <span
                        className={`badge badge-light-${item.isOptional ? 'success' : 'danger'}`}
                      >
                        {item.isOptional
                          ? intl.formatMessage({id: 'TOUR.EXTRAS.OPTIONAL'})
                          : intl.formatMessage({id: 'TOUR.EXTRAS.REQUIRED'})}
                      </span>
                    </td>
                    <td className='text-end'>
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.EDIT'})}</Tooltip>}
                      >
                        <button
                          className='btn btn-icon btn-light-primary btn-sm me-1'
                          onClick={() => handleEdit(item)}
                        >
                          <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                        </button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.DELETE'})}</Tooltip>}
                      >
                        <button
                          className='btn btn-icon btn-light-danger btn-sm'
                          onClick={() => handleDelete(item._id!)}
                        >
                          <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                        </button>
                      </OverlayTrigger>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  const isLoading = isPricingLoading || isExtrasLoading

  // If we're still loading, show spinner
  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center min-h-300px'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='card card-flush'>
      <div className='card-body'>
        {renderListView()}

        {/* Modal for adding/editing extras */}
        <Modal
          show={modalOpen}
          onHide={closeModal}
          size='lg'
          centered
          backdrop='static'
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {currentItem?._id
                ? intl.formatMessage({id: 'TOUR.EXTRAS.EDIT'})
                : intl.formatMessage({id: 'TOUR.EXTRAS.ADD'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentItem && (
              <ExtrasForm
                initialValues={currentItem}
                onSubmit={handleSubmit}
                onCancel={closeModal}
                intl={intl}
                tourPriceId={effectiveTourPriceId!}
                isSubmitting={isSaving}
              />
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  )
}

export default Extras
